/**
 * Compute the minimum price per price level from an availability map.
 *
 * @param {Object} availability - keyed by date string, each value is a day record
 *   with shape: { available, minPrice: number|null, priceLevel: 'cheap'|'normal'|'expensive'|null }
 *   where `minPrice` is the flat day-level price (sourced from `display_price`).
 * @returns {{ cheap: number|null, normal: number|null, expensive: number|null }}
 */
/**
 * Group available dates by price level.
 *
 * @param {Object} availability - keyed by date string (same shape as getMinPriceByLevel)
 * @returns {{ cheap: Date[], normal: Date[], expensive: Date[] }}
 */
export const groupDatesByPriceLevel = availability => {
  const result = { cheap: [], normal: [], expensive: [] };
  if (!availability) return result;

  Object.entries(availability).forEach(([dateKey, data]) => {
    if (!data?.available || !data?.priceLevel) return;
    const level = data.priceLevel;
    if (!(level in result)) return;
    result[level].push(new Date(`${dateKey}T00:00:00`));
  });

  return result;
};

export const getMinPriceByLevel = availability => {
  const result = { cheap: null, normal: null, expensive: null };
  if (!availability) return result;

  Object.values(availability).forEach(day => {
    if (!day || !day.available) return;
    const level = day.priceLevel;
    if (level !== 'cheap' && level !== 'normal' && level !== 'expensive') return;
    const price = day.minPrice;
    if (typeof price !== 'number') return;
    if (result[level] === null || price < result[level]) {
      result[level] = price;
    }
  });

  return result;
};
